import React, { useEffect, useState } from "react";
import { fetchTransactions } from "../api.js";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export default function MonthlyWeeklyViewPage() {
  const [items, setItems] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [openWeeks, setOpenWeeks] = useState({});
  const [darkMode, setDarkMode] = useState(false);

  // Searches
  const [search, setSearch] = useState(""); // general search
  const [nameSearch, setNameSearch] = useState(""); // name-only search

  // Swipe
  const [touchStartX, setTouchStartX] = useState(null);

  // Infinite scroll
  const [visibleWeeksCount, setVisibleWeeksCount] = useState(2);

  useEffect(() => {
    fetchTransactions().then(setItems);
  }, []);

  // Reset visible weeks when month changes
  useEffect(() => {
    setVisibleWeeksCount(2);
    setOpenWeeks({});
  }, [selectedYear, selectedMonth]);

  // Extract years
  const years = Array.from(
    new Set(items.map((t) => new Date(t.date).getFullYear()))
  ).sort();

  // Extract months for selected year
  const months = selectedYear
    ? Array.from(
        new Set(
          items
            .filter(
              (t) => new Date(t.date).getFullYear() === Number(selectedYear)
            )
            .map((t) => new Date(t.date).getMonth())
        )
      ).sort((a, b) => a - b)
    : [];

  // Filter by year/month
  const filtered = items.filter((t) => {
    const d = new Date(t.date);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;

    if (selectedYear && y !== Number(selectedYear)) return false;
    if (selectedMonth && m !== Number(selectedMonth)) return false;
    return true;
  });

  // Totals
  const totalIn = filtered
    .filter((t) => t.type === "in")
    .reduce((s, t) => s + t.amount, 0);
  const totalOut = filtered
    .filter((t) => t.type === "out")
    .reduce((s, t) => s + t.amount, 0);
  const net = totalIn - totalOut;

  // Week number
  function getWeekNumber(date) {
    return Math.ceil(new Date(date).getDate() / 7);
  }

  // Week range
  function getWeekRange(year, month, weekNumber) {
    const startDay = (weekNumber - 1) * 7 + 1;
    const endDay = weekNumber * 7;

    const lastDay = new Date(year, month, 0).getDate();

    const fromDate = new Date(year, month - 1, startDay);
    const toDate = new Date(year, month - 1, Math.min(endDay, lastDay));

    return {
      from: fromDate.toLocaleDateString(),
      to: toDate.toLocaleDateString(),
      fromWeekday: WEEKDAY_NAMES[fromDate.getDay()],
      toWeekday: WEEKDAY_NAMES[toDate.getDay()],
    };
  }

  // Group by week → days
  const weeklyGroups = {};
  if (selectedMonth) {
    filtered.forEach((t) => {
      const d = new Date(t.date);
      const week = getWeekNumber(t.date);
      const dayKey = d.toISOString().slice(0, 10);

      if (!weeklyGroups[week]) weeklyGroups[week] = {};
      if (!weeklyGroups[week][dayKey]) weeklyGroups[week][dayKey] = [];

      weeklyGroups[week][dayKey].push(t);
    });
  }

  const sortedWeekNumbers = selectedMonth
    ? Object.keys(weeklyGroups)
        .map(Number)
        .sort((a, b) => a - b)
    : [];

  // Toggle all weeks
  function toggleAll(open) {
    const newState = {};
    sortedWeekNumbers.forEach((w) => (newState[w] = open));
    setOpenWeeks(newState);
  }

  // Swipe handlers
  function handleTouchStart(e) {
    setTouchStartX(e.changedTouches[0].clientX);
  }

  function handleTouchEnd(e) {
    if (!touchStartX) return;

    const diff = touchStartX - e.changedTouches[0].clientX;

    if (diff > 50) goToNextMonth();
    if (diff < -50) goToPreviousMonth();

    setTouchStartX(null);
  }

  function goToNextMonth() {
    if (!selectedYear || !selectedMonth) return;

    let y = Number(selectedYear);
    let m = Number(selectedMonth);

    m++;
    if (m > 12) {
      m = 1;
      y++;
    }

    setSelectedYear(String(y));
    setSelectedMonth(String(m));
  }

  function goToPreviousMonth() {
    if (!selectedYear || !selectedMonth) return;

    let y = Number(selectedYear);
    let m = Number(selectedMonth);

    m--;
    if (m < 1) {
      m = 12;
      y--;
    }

    setSelectedYear(String(y));
    setSelectedMonth(String(m));
  }

  // Infinite scroll
  useEffect(() => {
    function onScroll() {
      const bottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;

      if (bottom && visibleWeeksCount < sortedWeekNumbers.length) {
        setVisibleWeeksCount((c) => c + 1);
      }
    }

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [visibleWeeksCount, sortedWeekNumbers.length]);

  // CSV export
  function exportCSV() {
    const rows = [["Date", "Name", "Type", "Amount", "Year", "Month", "Week"]];

    filtered.forEach((t) => {
      const d = new Date(t.date);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const w = getWeekNumber(t.date);

      rows.push([d.toLocaleDateString(), t.name, t.type, t.amount, y, m, w]);
    });

    const csv = rows
      .map((row) =>
        row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "expenses.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // PART 2 — Styles + Header UI

  const styles = {
    container: {
      padding: "15px",
      maxWidth: "900px",
      margin: "0 auto",
      fontFamily: "Arial, sans-serif",
      background: darkMode ? "#111" : "#fff",
      color: darkMode ? "#eee" : "#000",
      minHeight: "100vh",
      transition: "0.3s",
    },
    stickyBar: {
      position: "sticky",
      top: 0,
      background: darkMode ? "#222" : "#fff",
      padding: "10px 0",
      zIndex: 10,
      borderBottom: darkMode ? "1px solid #444" : "1px solid #ddd",
    },
    dropdownRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
    },
    dropdown: {
      flex: "1 1 150px",
      padding: "10px",
      fontSize: "16px",
      borderRadius: "6px",
      border: "1px solid #ccc",
    },
    button: {
      marginTop: "10px",
      padding: "10px 15px",
      background: "#333",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      marginRight: "10px",
    },
    darkToggle: {
      marginTop: "10px",
      padding: "10px 15px",
      background: darkMode ? "#444" : "#ddd",
      color: darkMode ? "#fff" : "#000",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
    },
    totalsBox: {
      background: darkMode ? "#222" : "#f7f7f7",
      padding: "15px",
      borderRadius: "8px",
      marginTop: "20px",
    },
    searchBox: {
      width: "100%",
      padding: "10px",
      marginTop: "10px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      fontSize: "16px",
    },
    showHideButton: {
      marginTop: "10px",
      padding: "10px 15px",
      background: "#555",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      marginRight: "10px",
    },
    swipeHint: {
      fontSize: "12px",
      opacity: 0.6,
      marginTop: "4px",
    },
    weekBox: {
      background: darkMode ? "#1a1a1a" : "#fafafa",
      padding: "12px",
      borderRadius: "8px",
      marginBottom: "15px",
      border: darkMode ? "1px solid #333" : "1px solid #ddd",
      transition: "0.3s",
    },
    weekHeader: {
      fontWeight: "bold",
      marginBottom: "8px",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
    },
    animatedSection: (isOpen) => ({
      maxHeight: isOpen ? "800px" : "0px",
      overflow: "hidden",
      transition: "max-height 0.4s ease",
    }),
    dayHeader: {
      marginTop: "8px",
      fontWeight: "bold",
      fontSize: "14px",
    },
    entry: {
      padding: "4px 0",
      borderBottom: darkMode ? "1px solid #333" : "1px solid #eee",
      transition: "0.3s",
    },
    inText: { color: "green", fontWeight: "bold" },
    outText: { color: "red", fontWeight: "bold" },
    mobileText: { fontSize: "14px" },
    weekTotals: {
      marginTop: "8px",
      borderTop: darkMode ? "1px solid #333" : "1px solid #ddd",
      paddingTop: "6px",
      fontSize: "14px",
    },
    backToTop: {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      background: "#333",
      color: "#fff",
      padding: "12px 15px",
      borderRadius: "50%",
      cursor: "pointer",
      fontSize: "18px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
    },
  };

  const [yearNameSearch, setYearNameSearch] = useState("");
  const [yearSearchResults, setYearSearchResults] = useState([]);
  function handleYearSearch() {
    if (!selectedYear) {
      alert("Please select a year first.");
      return;
    }

    const results = items.filter((t) => {
      const d = new Date(t.date);
      const y = d.getFullYear();

      return (
        y === Number(selectedYear) &&
        t.name.toLowerCase().includes(yearNameSearch.toLowerCase())
      );
    });

    setYearSearchResults(results);
  }

  return (
    <div
      style={styles.container}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <h2>Monthly & Weekly View</h2>

      {/* Sticky Header */}
      <div style={styles.stickyBar}>
        <div style={styles.dropdownRow}>
          <select
            style={styles.dropdown}
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setSelectedMonth("");
            }}
          >
            <option value="">All Years</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          {selectedYear && (
            <select
              style={styles.dropdown}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">All Months</option>
              {months.map((m) => (
                <option key={m} value={m + 1}>
                  {MONTH_NAMES[m]}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <button style={styles.button} onClick={() => window.print()}>
            Print / Export PDF
          </button>

          <button style={styles.button} onClick={exportCSV}>
            Export CSV
          </button>

          <button
            style={styles.darkToggle}
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        <div style={styles.swipeHint}>Swipe left/right to change month</div>
      </div>

      <div style={styles.totalsBox}>
        <p>
          Total In: <span style={styles.inText}>{totalIn}</span>
        </p>
        <p>
          Total Out: <span style={styles.outText}>{totalOut}</span>
        </p>
        <p>
          <strong>Net: {net}</strong>
        </p>
      </div>

      <input
        style={styles.searchBox}
        placeholder="Search inside weeks (name, type, amount)..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* <input
        style={styles.searchBox}
        placeholder="Search by name only..."
        value={nameSearch}
        onChange={(e) => setNameSearch(e.target.value)}
      /> */}

      <input
        style={styles.searchBox}
        placeholder="Search names for entire year..."
        value={yearNameSearch}
        onChange={(e) => setYearNameSearch(e.target.value)}
      />

      <button style={styles.button} onClick={handleYearSearch}>
        Search Year
      </button>
      {yearSearchResults.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <h3>Search Results for {selectedYear}</h3>

          {yearSearchResults.map((t) => (
            <div key={t._id} style={styles.entry}>
              <span style={styles.mobileText}>
                {new Date(t.date).toLocaleDateString()} — {t.name} —{" "}
                {t.type === "in" ? (
                  <span style={styles.inText}>IN {t.amount}</span>
                ) : (
                  <span style={styles.outText}>OUT {t.amount}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {selectedMonth && (
        <div>
          <button style={styles.showHideButton} onClick={() => toggleAll(true)}>
            Show All Weeks
          </button>
          <button
            style={styles.showHideButton}
            onClick={() => toggleAll(false)}
          >
            Hide All Weeks
          </button>
        </div>
      )}
      {/* Weekly → Daily breakdown with infinite scroll */}
      {selectedMonth ? (
        sortedWeekNumbers.slice(0, visibleWeeksCount).map((week) => {
          const { from, to, fromWeekday, toWeekday } = getWeekRange(
            Number(selectedYear),
            Number(selectedMonth),
            Number(week)
          );

          const isOpen = openWeeks[week];

          // Compute weekly totals
          let weekIn = 0;
          let weekOut = 0;

          const dayKeys = Object.keys(weeklyGroups[week]).sort();

          dayKeys.forEach((dayKey) => {
            weeklyGroups[week][dayKey].forEach((t) => {
              if (t.type === "in") weekIn += t.amount;
              else weekOut += t.amount;
            });
          });

          const weekNet = weekIn - weekOut;

          return (
            <div key={week} style={styles.weekBox}>
              <div
                style={styles.weekHeader}
                onClick={() =>
                  setOpenWeeks((prev) => ({ ...prev, [week]: !prev[week] }))
                }
              >
                <span>
                  Week {week} — {fromWeekday} {from} to {toWeekday} {to}
                </span>
                <span>{isOpen ? "▲" : "▼"}</span>
              </div>

              <div style={styles.animatedSection(isOpen)}>
                {dayKeys.map((dayKey) => {
                  const dateObj = new Date(dayKey);
                  const weekday = WEEKDAY_NAMES[dateObj.getDay()];
                  const prettyDate = dateObj.toLocaleDateString();

                  const dayEntries = weeklyGroups[week][dayKey].filter((t) => {
                    const generalText =
                      `${t.name} ${t.type} ${t.amount}`.toLowerCase();
                    const nameText = t.name.toLowerCase();

                    const generalMatch = generalText.includes(
                      search.toLowerCase()
                    );
                    const nameMatch = nameText.includes(
                      nameSearch.toLowerCase()
                    );

                    return generalMatch || nameMatch;
                  });

                  if (dayEntries.length === 0) return null;

                  return (
                    <div key={dayKey}>
                      <div style={styles.dayHeader}>
                        {weekday} {prettyDate}
                      </div>
                      {dayEntries.map((t) => (
                        <div key={t._id} style={styles.entry}>
                          <span style={styles.mobileText}>
                            {t.name} —{" "}
                            {t.type === "in" ? (
                              <span style={styles.inText}>IN {t.amount}</span>
                            ) : (
                              <span style={styles.outText}>OUT {t.amount}</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {/* Weekly totals at bottom */}
                <div style={styles.weekTotals}>
                  <div>
                    Week Total In: <span style={styles.inText}>{weekIn}</span>
                  </div>
                  <div>
                    Week Total Out:{" "}
                    <span style={styles.outText}>{weekOut}</span>
                  </div>
                  <div>
                    <strong>Week Net: {weekNet}</strong>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div>
          <h3>All-Time Records</h3>
          {filtered.map((t) => (
            <div key={t._id} style={styles.entry}>
              <span style={styles.mobileText}>
                {new Date(t.date).toLocaleDateString()} — {t.name} —{" "}
                {t.type === "in" ? (
                  <span style={styles.inText}>IN {t.amount}</span>
                ) : (
                  <span style={styles.outText}>OUT {t.amount}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {/* Back to top */}
      <div
        style={styles.backToTop}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        ↑
      </div>
    </div>
  );
}
